/**
 * AudioPlayerScreen — transport + scrubber + synced transcript (AUDIO-LESSONS-003/004/005/006/008).
 *
 * Drives the `audioPlayer` (expo-audio) service: loads the
 * server-resolved rendition, exposes play/pause + a seek scrubber + ±skip,
 * renders the karaoke `SyncedTranscript`, and offers a "Taught in" teaching-
 * language toggle that re-fetches the episode with a `?teachingLanguage`
 * override while preserving the playback position. Playback is premium-gated via
 * FeatureGate (AUDIO-LESSONS-007) and resumes from saved progress
 * (AUDIO-LESSONS-008). It does NOT dispose the track on unmount — background
 * playback survives leaving the screen.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import Slider from '@react-native-community/slider';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';

import Ionicons from '../components/ui/Ionicons';
import { ScreenHeader } from '../components/ui/ScreenHeader';
import { WebContainer } from '../components/ui/WebContainer';
import { FeatureGate, useFeatureAccess } from '../components/FeatureGate';
import { SyncedTranscript } from '../components/audio/SyncedTranscript';
import { useDisplayTheme } from '../hooks/useDisplayTheme';
import { useResolveBilingual, slugToTitle } from '../hooks/useResolveBilingual';
import { useAudioProgress } from '../hooks/useAudioProgress';
import { formatDuration } from '../utils/formatDuration';
import { errorMessage } from '../generated/api/endpoint-errors';
import * as audioPlayer from '../services/audioPlayer';
import { SKIP_STEP_MS } from '../services/audioPlayer';
import { useAudioLessonsStore, getEpisodeProgress } from '../store/audioLessonsStore';
import {
  selectCurrentAudioEpisode,
  selectCurrentAudioRendition,
  selectCurrentAudioTranscript,
  selectCurrentAudioEpisodeSlug,
  selectAudioTeachingLanguage,
  selectAudioMissingTeachingLanguages,
  selectAudioEpisodeError,
  selectIsAudioEpisodeForbidden,
  selectFetchAudioEpisode,
  selectResetAudioPlayer,
  selectClearAudioEpisodeError,
  selectSaveAudioProgress,
} from '../store/audioLessonsSelectors';
import { useUserPreferencesStore } from '../store/userPreferencesStore';
import { selectResolvedCourse } from '../store/userPreferencesSelectors';
import { createLogger } from '../utils/logger';
import type { TeachingLanguage } from '../types/audioTranscript';

const log = createLogger('AudioPlayerScreen');

const TEACHING_OPTIONS: readonly TeachingLanguage[] = ['source', 'target'];
const SAVE_THROTTLE_MS = 10_000;
const SKIP_SECONDS = Math.round(SKIP_STEP_MS / 1000);

/**
 * Surface a player failure instead of letting it become a silent unhandled
 * rejection. The most common cause in dev is a stale build missing the
 * `expo-audio` native module (rebuild the dev-client — a Metro reload can't add
 * native code); also covers an unreachable audio URL or an audio-session error.
 */
function reportPlayerError(action: string, err: unknown, context?: Record<string, unknown>): void {
  log.warn(`audioPlayer.${action} failed`, {
    ...context,
    err: err instanceof Error ? err.message : String(err),
  });
}

export function AudioPlayerScreen() {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();
  const navigation = useNavigation();
  const route = useRoute();
  const slug = (route.params as { slug: string }).slug;
  const resolve = useResolveBilingual();

  // ── Store state ──────────────────────────────────────────────────
  const episode = useAudioLessonsStore(selectCurrentAudioEpisode);
  const rendition = useAudioLessonsStore(selectCurrentAudioRendition);
  const transcript = useAudioLessonsStore(selectCurrentAudioTranscript);
  const currentSlug = useAudioLessonsStore(selectCurrentAudioEpisodeSlug);
  const teachingLanguage = useAudioLessonsStore(selectAudioTeachingLanguage);
  const missing = useAudioLessonsStore(selectAudioMissingTeachingLanguages);
  const errorInfo = useAudioLessonsStore(selectAudioEpisodeError);
  const isForbidden = useAudioLessonsStore(selectIsAudioEpisodeForbidden);
  const fetchEpisode = useAudioLessonsStore(selectFetchAudioEpisode);
  const resetPlayer = useAudioLessonsStore(selectResetAudioPlayer);
  const clearError = useAudioLessonsStore(selectClearAudioEpisodeError);
  const saveProgress = useAudioLessonsStore(selectSaveAudioProgress);
  const resolvedCourse = useUserPreferencesStore(selectResolvedCourse);
  const { hasAccess } = useFeatureAccess('audioLessons');

  const isOurs = currentSlug === slug;
  const audioUrl = isOurs ? rendition?.audioUrl ?? null : null;
  const durationFromRendition = isOurs ? rendition?.durationMs ?? 0 : 0;

  // ── Live playback (expo-audio via the progress store) ────────────
  const { positionMs: livePositionMs, durationMs: liveDurationMsRaw, playing } = useAudioProgress();
  const liveDurationMs = liveDurationMsRaw > 0 ? liveDurationMsRaw : durationFromRendition;

  // ── Refs for save-on-leave + position preservation across swaps ──
  const positionRef = useRef(0);
  const completedRef = useRef(false);
  const lastSaveRef = useRef(0);
  const pendingSeekRef = useRef<number | null>(null);
  const pendingPlayRef = useRef(false);
  // Whether THIS screen owns the loaded track right now — read through a ref so
  // the unmount-only flush stays unmount-only (no dep churn) while still gating
  // on ownership (the global useProgress reflects whatever track is loaded).
  const ownsTrackRef = useRef(false);
  positionRef.current = livePositionMs;
  ownsTrackRef.current = isOurs && !!audioUrl;

  const title = isOurs && episode ? resolve(episode.title) || slugToTitle(slug) : slugToTitle(slug);
  const seriesTitle = isOurs && episode ? slugToTitle(episode.seriesSlug) : 'Fluencia';

  // ── Load the episode on mount / slug change ──────────────────────
  useEffect(() => {
    resetPlayer();
    completedRef.current = false;
    pendingSeekRef.current = getEpisodeProgress(slug)?.positionMs ?? 0;
    pendingPlayRef.current = false;
    void fetchEpisode(slug);
  }, [slug, resetPlayer, fetchEpisode]);

  // ── Load the resolved rendition into track-player + apply pending seek ──
  useEffect(() => {
    if (!audioUrl) return;
    const desiredId = `${slug}:${teachingLanguage ?? 'source'}`;
    // Returning to a still-playing episode: the desired track is already loaded.
    // Rebind to LIVE track-player state — do NOT reload or seek (that would
    // restart playback and rewind to a stale saved position). AUDIO-LESSONS-006.
    if (audioPlayer.getLoadedTrackId() === desiredId) {
      pendingSeekRef.current = null;
      pendingPlayRef.current = false;
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        await audioPlayer.load({
          id: desiredId,
          url: audioUrl,
          title,
          artist: seriesTitle,
          durationMs: durationFromRendition || undefined,
        });
        if (cancelled) return;
        const seekMs = pendingSeekRef.current;
        if (seekMs != null && seekMs > 0) await audioPlayer.seek(seekMs);
        pendingSeekRef.current = null;
        if (pendingPlayRef.current) {
          await audioPlayer.play();
          pendingPlayRef.current = false;
        }
      } catch (err) {
        // A failed load leaves no queued track, so the transport silently does
        // nothing — log it so the cause (e.g. missing native module) is visible.
        reportPlayerError('load', err, { slug, audioUrl });
      }
    })();
    return () => {
      cancelled = true;
    };
    // Reload only when the actual rendition URL changes (initial load + swap).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioUrl]);

  // ── Progress: throttled save while playing + mark complete at end ──
  useEffect(() => {
    if (!isOurs || !audioUrl) return;
    if (liveDurationMs > 0 && livePositionMs >= liveDurationMs - 500) {
      if (!completedRef.current) {
        completedRef.current = true;
        void saveProgress(slug, liveDurationMs, true);
      }
      return;
    }
    if (livePositionMs > 0) completedRef.current = false;
    const now = Date.now();
    if (playing && now - lastSaveRef.current > SAVE_THROTTLE_MS) {
      lastSaveRef.current = now;
      void saveProgress(slug, livePositionMs, false);
    }
  }, [livePositionMs, liveDurationMs, playing, isOurs, audioUrl, slug, saveProgress]);

  // ── Flush progress on pause (true → false transition) ────────────
  const prevPlayingRef = useRef(false);
  useEffect(() => {
    if (prevPlayingRef.current && !playing && isOurs && audioUrl && positionRef.current > 0) {
      void saveProgress(slug, positionRef.current, completedRef.current);
    }
    prevPlayingRef.current = playing;
  }, [playing, isOurs, audioUrl, slug, saveProgress]);

  // ── Flush progress on leave/unmount (background continues playing) ──
  // Guarded on ownership so we never persist a foreign episode's playhead (the
  // global useProgress keeps updating positionRef even when this screen no
  // longer owns the loaded track).
  useEffect(() => {
    return () => {
      if (ownsTrackRef.current && positionRef.current > 0) {
        void saveProgress(slug, positionRef.current, completedRef.current);
      }
    };
  }, [slug, saveProgress]);

  // ── Scrubber (local drag value so it doesn't fight live position) ──
  const [scrubMs, setScrubMs] = useState<number | null>(null);
  const handleSlide = useCallback((seconds: number) => setScrubMs(Math.round(seconds * 1000)), []);
  const handleSlideComplete = useCallback(
    (seconds: number) => {
      const ms = Math.round(seconds * 1000);
      setScrubMs(null);
      audioPlayer.seek(ms).catch((e) => reportPlayerError('seek', e));
    },
    [],
  );

  const handlePlayPause = useCallback(() => {
    const action = playing ? audioPlayer.pause() : audioPlayer.play();
    action.catch((e) => reportPlayerError(playing ? 'pause' : 'play', e));
  }, [playing]);

  const handleSkip = useCallback(
    (deltaMs: number) => {
      const target = Math.min(Math.max(0, livePositionMs + deltaMs), liveDurationMs || Number.MAX_SAFE_INTEGER);
      audioPlayer.seek(target).catch((e) => reportPlayerError('seek', e));
    },
    [livePositionMs, liveDurationMs],
  );

  const handleTranscriptSeek = useCallback((ms: number) => {
    audioPlayer.seek(ms).catch((e) => reportPlayerError('seek', e));
  }, []);

  const handleRetry = useCallback(() => {
    clearError();
    pendingSeekRef.current = getEpisodeProgress(slug)?.positionMs ?? 0;
    void fetchEpisode(slug);
  }, [clearError, fetchEpisode, slug]);

  // ── Teaching-language toggle ─────────────────────────────────────
  const [swapNotice, setSwapNotice] = useState(false);
  const availableOptions = useMemo(() => {
    // Authoritative: the episode lists which teaching-language renditions exist,
    // so the toggle shows only real options on first paint.
    const fromServer = episode?.availableTeachingLanguages;
    if (fromServer && fromServer.length > 0) {
      return TEACHING_OPTIONS.filter((o) => fromServer.includes(o));
    }
    // Fallback (older episodes without the field): infer from the server's silent
    // fallback recorded in `missing`.
    return TEACHING_OPTIONS.filter((o) => !missing.includes(o));
  }, [episode, missing]);
  const showToggle = availableOptions.length > 1;

  const languageLabel = useCallback(
    (tl: TeachingLanguage) =>
      tl === 'source'
        ? resolvedCourse.sourceLocale.native.language
        : resolvedCourse.targetLocale.native.language,
    [resolvedCourse],
  );

  const handleSwapLanguage = useCallback(
    async (option: TeachingLanguage) => {
      if (option === teachingLanguage) return;
      setSwapNotice(false);
      // Preserve position + play state across the swap (AUDIO-LESSONS-005). A
      // failed position read shouldn't abort the swap — fall back to 0.
      let positionMs = 0;
      try {
        ({ positionMs } = await audioPlayer.getProgressMs());
      } catch (e) {
        reportPlayerError('getProgressMs', e);
      }
      pendingSeekRef.current = positionMs;
      pendingPlayRef.current = playing;
      const ok = await fetchEpisode(slug, { teachingLanguage: option });
      // Fell back (requested rendition absent) → surface a transient notice.
      const resolvedLang = useAudioLessonsStore.getState().teachingLanguage;
      if (!ok || resolvedLang !== option) {
        pendingSeekRef.current = null;
        pendingPlayRef.current = false;
        setSwapNotice(true);
      }
    },
    [teachingLanguage, playing, fetchEpisode, slug],
  );

  const handleBack = useCallback(() => navigation.goBack(), [navigation]);

  // ── Render ───────────────────────────────────────────────────────
  const displayPositionMs = scrubMs ?? livePositionMs;
  const sliderMax = liveDurationMs > 0 ? liveDurationMs / 1000 : 1;

  const ready = isOurs && !!rendition;
  // Premium-gated: free user (no access) or the server forbade a premium episode (403).
  const gated = !hasAccess || isForbidden;
  const showError = isOurs && !!errorInfo && !rendition && !isForbidden;
  const showLoading = !ready && !showError && !gated;

  return (
    <View style={{ flex: 1, backgroundColor: theme.background }}>
      <ScreenHeader title={title} onBack={handleBack} />
      <WebContainer variant="page" style={{ flex: 1 }}>
        <View style={styles.body}>
          {showError && (
            <View
              testID="audio-player-error"
              style={[styles.errorBanner, { backgroundColor: theme.card, borderColor: theme.error }]}
            >
              <Text allowFontScaling={false} style={{ flex: 1, fontSize: 14 * fontScale, color: theme.text, ...ffs }}>
                {errorInfo ? errorMessage(errorInfo) : t('audioLessons.player.loadError')}
              </Text>
              <TouchableOpacity
                testID="audio-player-error-retry"
                accessibilityRole="button"
                accessibilityLabel={t('audioLessons.player.retry')}
                onPress={handleRetry}
                style={[styles.retryButton, { borderColor: theme.border }]}
              >
                <Text
                  allowFontScaling={false}
                  style={{ fontSize: 14 * fontScale, color: theme.text, fontWeight: '600', ...ffs }}
                >
                  {t('audioLessons.player.retry')}
                </Text>
              </TouchableOpacity>
            </View>
          )}

          {showLoading && (
            <ActivityIndicator size="small" color={theme.primary} style={{ paddingVertical: 32 }} />
          )}

          {(ready || gated) && (
            <FeatureGate feature="audioLessons">
              <View testID="audio-player-screen" style={{ flex: 1 }}>
                {episode && episode.grammarFocus.length > 0 && (
                  <Text
                    allowFontScaling={false}
                    style={{ fontSize: 14 * fontScale, color: theme.textSecondary, marginBottom: 12, ...ffs }}
                  >
                    {episode.grammarFocus}
                  </Text>
                )}

                {showToggle && (
                  <View style={styles.toggleRow}>
                    <Text
                      allowFontScaling={false}
                      style={{ fontSize: 13 * fontScale, color: theme.textSecondary, ...ffs }}
                    >
                      {t('audioLessons.player.taughtIn')}
                    </Text>
                    <View style={styles.toggleGroup}>
                      {availableOptions.map((option) => {
                        const active = option === teachingLanguage;
                        return (
                          <TouchableOpacity
                            key={option}
                            testID={`audio-player-teaching-${option}`}
                            accessibilityRole="button"
                            accessibilityState={{ selected: active }}
                            accessibilityLabel={languageLabel(option)}
                            onPress={() => void handleSwapLanguage(option)}
                            style={[
                              styles.toggleButton,
                              { borderColor: theme.border },
                              active && { backgroundColor: theme.primary, borderColor: theme.primary },
                            ]}
                          >
                            <Text
                              allowFontScaling={false}
                              style={{
                                fontSize: 13 * fontScale,
                                fontWeight: '600',
                                color: active ? '#FFFFFF' : theme.text,
                                ...ffs,
                              }}
                            >
                              {languageLabel(option)}
                            </Text>
                          </TouchableOpacity>
                        );
                      })}
                    </View>
                  </View>
                )}

                {swapNotice && (
                  <Text
                    testID="audio-player-swap-notice"
                    allowFontScaling={false}
                    style={{ fontSize: 12 * fontScale, color: theme.textSecondary, marginBottom: 8, ...ffs }}
                  >
                    {t('audioLessons.player.renditionUnavailable')}
                  </Text>
                )}

                {/* Transport */}
                <View style={styles.transport}>
                  <TouchableOpacity
                    testID="audio-player-skip-back"
                    accessibilityRole="button"
                    accessibilityLabel={t('audioLessons.player.skipBack', { seconds: SKIP_SECONDS })}
                    onPress={() => handleSkip(-SKIP_STEP_MS)}
                  >
                    <Ionicons name="play-back" size={28} color={theme.text} />
                  </TouchableOpacity>
                  <TouchableOpacity
                    testID="audio-player-play-pause"
                    accessibilityRole="button"
                    accessibilityLabel={playing ? t('audioLessons.player.pause') : t('audioLessons.player.play')}
                    onPress={handlePlayPause}
                    style={[styles.playButton, { backgroundColor: theme.primary }]}
                  >
                    <Ionicons name={playing ? 'pause' : 'play'} size={32} color="#FFFFFF" />
                  </TouchableOpacity>
                  <TouchableOpacity
                    testID="audio-player-skip-forward"
                    accessibilityRole="button"
                    accessibilityLabel={t('audioLessons.player.skipForward', { seconds: SKIP_SECONDS })}
                    onPress={() => handleSkip(SKIP_STEP_MS)}
                  >
                    <Ionicons name="play-forward" size={28} color={theme.text} />
                  </TouchableOpacity>
                </View>

                {/* Scrubber */}
                <View style={styles.scrubberRow}>
                  <Text allowFontScaling={false} style={{ fontSize: 12 * fontScale, color: theme.textSecondary, ...ffs }}>
                    {formatDuration(displayPositionMs)}
                  </Text>
                  <Slider
                    testID="audio-player-scrubber"
                    style={styles.slider}
                    minimumValue={0}
                    maximumValue={sliderMax}
                    value={displayPositionMs / 1000}
                    onValueChange={handleSlide}
                    onSlidingComplete={handleSlideComplete}
                    minimumTrackTintColor={theme.primary}
                    maximumTrackTintColor={theme.border}
                    thumbTintColor={theme.primary}
                    accessibilityLabel={t('audioLessons.player.seek')}
                  />
                  <Text allowFontScaling={false} style={{ fontSize: 12 * fontScale, color: theme.textSecondary, ...ffs }}>
                    {formatDuration(liveDurationMs)}
                  </Text>
                </View>

                {/* Transcript */}
                {transcript && (
                  <View style={styles.transcriptWrap}>
                    <Text
                      allowFontScaling={false}
                      style={{ fontSize: 13 * fontScale, fontWeight: '600', color: theme.textSecondary, marginBottom: 8, ...ffs }}
                    >
                      {t('audioLessons.player.transcript')}
                    </Text>
                    <SyncedTranscript
                      transcript={transcript}
                      sourceLocale={resolvedCourse.sourceLocale.code}
                      onSeek={handleTranscriptSeek}
                    />
                  </View>
                )}
              </View>
            </FeatureGate>
          )}
        </View>
      </WebContainer>
    </View>
  );
}

const styles = StyleSheet.create({
  body: { flex: 1, padding: 16 },
  toggleRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  toggleGroup: { flexDirection: 'row', gap: 8 },
  toggleButton: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, borderWidth: 1 },
  transport: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 28, marginVertical: 12 },
  playButton: { width: 64, height: 64, borderRadius: 32, alignItems: 'center', justifyContent: 'center' },
  scrubberRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  slider: { flex: 1, height: 40 },
  transcriptWrap: { flex: 1, marginTop: 16 },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    gap: 8,
    marginBottom: 8,
  },
  retryButton: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 6, borderWidth: 1 },
});
